import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Mail, Eye, ThumbsDown, X } from "lucide-react";
import toast from "react-hot-toast";
import { api, API_BASE, getAuthHeaders, type ApiResponse } from "@/api/client";

interface NewsletterHistoryRow {
  sendId: string;
  campaignId: string;
  status: string;
  sentAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  subjectVariant: number | null;
  wave: number | null;
  subject: string;
  email: string;
  hasSections: number;
  recommendation: {
    recId: string;
    tripIds: string[] | null;
    meta: Array<{ tripId: string; title: string }> | null;
  } | null;
}

/**
 * "Newsletters" on the customer detail (Řez 6): every campaign sent to this email with
 * open/click status + subject variant, a faithful "View email" reconstruction (frozen
 * sections + the send-time card snapshot) and a "Bad recommendation" flag that feeds the
 * learning loop.
 */
export default function NewslettersPanel({ customerId }: { customerId: string }) {
  const [viewHtml, setViewHtml] = useState<string | null>(null);
  const [flagFor, setFlagFor] = useState<NewsletterHistoryRow | null>(null);
  const [flagNote, setFlagNote] = useState("");

  const { data } = useQuery({
    queryKey: ["customer-newsletters", customerId],
    queryFn: () =>
      api.get<ApiResponse<NewsletterHistoryRow[]>>(
        `/api/admin/customers/${customerId}/newsletters`,
      ),
    enabled: !!customerId,
  });

  const rows = data?.data ?? [];
  if (!rows.length) return null;

  const viewEmail = async (row: NewsletterHistoryRow) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/newsletter-studio/campaigns/${row.campaignId}/reconstruct?email=${encodeURIComponent(row.email)}`,
        { credentials: "include", headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error("Reconstruction failed");
      setViewHtml(await res.text());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load email");
    }
  };

  const flagRecommendation = async () => {
    if (!flagFor?.recommendation || !flagNote.trim()) return;
    await api.post("/api/admin/newsletter-studio/feedback", {
      scope: "recommendation",
      refId: flagFor.recommendation.recId,
      note: flagNote,
      meta: { campaignId: flagFor.campaignId, email: flagFor.email },
    });
    toast.success("Flagged — the analyst will learn from this");
    setFlagFor(null);
    setFlagNote("");
  };

  return (
    <div className="card">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
        <Mail className="text-primary-600 h-5 w-5" />
        Newsletters
      </h2>
      <div className="divide-y divide-gray-100">
        {rows.map((r) => (
          <div key={r.sendId} className="flex items-center gap-3 py-2 text-sm">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-gray-800">{r.subject}</p>
              <p className="text-xs text-gray-400">
                {r.sentAt?.slice(0, 10) ?? r.status}
                {r.wave != null && ` · wave ${r.wave}`}
                {r.subjectVariant != null && ` · variant ${r.subjectVariant + 1}`}
              </p>
            </div>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                r.clickedAt
                  ? "bg-green-100 text-green-700"
                  : r.openedAt
                    ? "bg-blue-100 text-blue-700"
                    : "bg-gray-100 text-gray-500"
              }`}
            >
              {r.clickedAt ? "clicked" : r.openedAt ? "opened" : r.status}
            </span>
            <button
              onClick={() => viewEmail(r)}
              className="text-primary-600 flex items-center gap-1 text-xs hover:underline"
            >
              <Eye className="h-3.5 w-3.5" /> View email
            </button>
            {r.recommendation && (
              <button
                onClick={() => setFlagFor(r)}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-600"
                title="Bad recommendation — tell the engine why"
              >
                <ThumbsDown className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>

      {viewHtml && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6">
          <div className="flex h-full w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2">
              <span className="text-sm font-medium text-gray-700">Sent email reconstruction</span>
              <button
                onClick={() => setViewHtml(null)}
                className="p-1 text-gray-400 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <iframe
              title="Sent email"
              srcDoc={viewHtml}
              sandbox=""
              className="h-full w-full border-0"
            />
          </div>
        </div>
      )}

      {flagFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6">
          <div className="w-full max-w-md space-y-3 rounded-xl bg-white p-5">
            <h3 className="font-semibold text-gray-900">Bad recommendation</h3>
            <p className="text-sm text-gray-500">
              Recommended: {flagFor.recommendation?.meta?.map((m) => m.title).join(", ") || "—"}
            </p>
            <textarea
              value={flagNote}
              onChange={(e) => setFlagNote(e.target.value)}
              rows={3}
              placeholder="Why was this wrong for them? (e.g. 'already did northern Vietnam twice')"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setFlagFor(null)} className="btn-secondary">
                Cancel
              </button>
              <button
                onClick={flagRecommendation}
                disabled={!flagNote.trim()}
                className="btn-primary"
              >
                Flag it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
